import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ShoppingCart, Trash2, XCircle, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fmtDate, today } from "@/lib/api";
import { downloadCsv } from "@/lib/exportCsv";
import { PageHeader, EmptyState, Money, TypeBadge } from "@/components/common";
import { PURCHASE_PAYMENT_TYPES, can } from "@/lib/labels";
import { PurchaseDialog } from "@/components/dialogs";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const inputCls = "h-10 bg-[#0F0F12] border-[#1A1A22] focus:border-blue-500 focus:ring-1 focus:ring-blue-500";
const labelCls = "text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400";

export default function Purchases() {
  const { user } = useAuth();
  const [purchases, setPurchases] = useState(null);
  const [cars, setCars] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [cancelP, setCancelP] = useState(null);
  const [editP, setEditP] = useState(null);
  const [payP, setPayP] = useState(null);
  const [month, setMonth] = useState("");

  const load = useCallback(() => {
    Promise.all([api.get("/purchases"), api.get("/cars")]).then(([p, c]) => {
      setPurchases(p.data);
      setCars(c.data);
    });
  }, []);
  useEffect(() => { load(); api.get("/branches").then((r) => setBranches(r.data)); }, [load]);

  const purchasedIds = new Set((purchases || []).filter((p) => p.status !== "cancelled").map((p) => p.car_id));
  const eligible = cars.filter((c) => !purchasedIds.has(c.id) && c.status !== "terjual" && !c.is_konsinyasi);
  const filteredPurchases = month ? (purchases || []).filter((p) => p.date.startsWith(month)) : purchases || [];

  const exportPurchases = () => {
    const carName = (id) => {
      const c = cars.find((x) => x.id === id);
      return c ? `${c.merk} ${c.model} ${c.color || ""} ${c.year || ""}`.trim() : "-";
    };
    downloadCsv(`pembelian-${month || "semua"}.csv`,
      ["Tanggal", "Mobil", "Tipe", "Harga Beli", "Dibayar", "Penjual", "Status"],
      filteredPurchases.map((p) => [p.date, carName(p.car_id), p.payment_type === "dp" ? "Down Payment" : "Cash Lunas", p.price, p.dp_amount || 0, p.seller_name || "-", p.status]));
    toast.success("File Excel (CSV) pembelian diunduh");
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus catatan pembelian ini?")) return;
    try {
      await api.delete(`/purchases/${id}`);
      toast.success("Pembelian dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const markPaidOff = async (p) => {
    try {
      await api.put(`/purchases/${p.id}`, { paid_off: true });
      toast.success("Pembelian ditandai lunas");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="purchases-page">
      <PageHeader title="Pembelian" subtitle="Catatan pembelian unit (Down Payment / Cash Lunas)">
        <Button data-testid="export-purchases-button" variant="outline" onClick={exportPurchases} className="border-[#26262E] text-zinc-300 hover:bg-[#121218] h-10">
          Unduh Excel
        </Button>
        <Input data-testid="purchase-month-filter" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 h-10 bg-[#0F0F12] border-[#1A1A22]" />
        {can(user, "kelola_pembelian") && (
          <Button data-testid="add-purchase-button" onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold rounded-md">
            <Plus className="w-4 h-4 mr-1" /> Catat Pembelian
          </Button>
        )}
      </PageHeader>

      {!purchases ? (
        <div className="h-64 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />
      ) : purchases.length === 0 ? (
        <EmptyState icon={ShoppingCart} title="Belum ada pembelian" desc="Catat pembelian unit pertama Anda" />
      ) : (
        <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md overflow-x-auto fade-up">
          <table className="w-full text-sm min-w-[760px]" data-testid="purchases-table">
            <thead>
              <tr className="border-b border-[#1A1A22] text-left">
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Mobil</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Tipe</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Harga Beli</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Dibayar</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Status</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Tanggal</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filteredPurchases.map((p) => (
                <tr key={p.id} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
                  <td className="px-4 py-3">
                    <Link to={`/mobil/${p.car_id}`} className="font-medium text-zinc-100 hover:text-blue-500 transition-colors duration-200">{p.car_name}</Link>
                    <p className="text-xs text-zinc-500">{p.seller_name ? `dari ${p.seller_name}` : ""}</p>
                    {p.created_by_name && <p className="text-[10px] text-zinc-600">dicatat oleh {p.created_by_name}</p>}
                    {p.tax_status && (
                      <p className="text-[10px] text-zinc-500 mt-0.5">Pajak: {p.tax_status === "hidup" ? "Hidup" : "Mati"}{p.tax_due_date ? ` s.d. ${fmtDate(p.tax_due_date)}` : ""}</p>
                    )}
                    {p.status === "cancelled" && p.cancel_reason && (
                      <p className="text-[10px] text-rose-500 mt-0.5">{p.cancel_reason}</p>
                    )}
                  </td>
                  <td className="px-4 py-3"><TypeBadge label={PURCHASE_PAYMENT_TYPES[p.payment_type]} /></td>
                  <td className="px-4 py-3 text-right"><Money value={p.price} className="text-zinc-100" /></td>
                  <td className="px-4 py-3 text-right">{p.payment_type === "dp" ? <Money value={p.dp_amount} className="text-zinc-300" /> : <span className="text-zinc-600">-</span>}</td>
                  <td className="px-4 py-3">
                    {p.status === "cancelled" ? (
                      <span className="text-rose-500 text-xs font-semibold uppercase tracking-wider">Dibatalkan</span>
                    ) : p.payment_type === "cash" || p.paid_off ? (
                      <span className="text-emerald-500 text-xs font-semibold uppercase tracking-wider">Lunas</span>
                    ) : !can(user, "kelola_pembelian") ? (
                      <span className="text-blue-500 text-xs font-semibold uppercase tracking-wider">Belum Lunas</span>
                    ) : (
                      <div className="space-y-0.5">
                        <p className="text-blue-500 text-xs font-semibold uppercase tracking-wider">Belum Lunas</p>
                        <p className="text-[10px] text-zinc-500 font-mono">Sisa {new Intl.NumberFormat("id-ID").format(Math.max(0, p.price - (p.dp_amount || 0)))}</p>
                        <div className="flex gap-2">
                          <button data-testid={`pay-${p.id}`} onClick={() => setPayP(p)} className="text-[10px] text-emerald-500 font-semibold uppercase tracking-wider hover:text-emerald-400 transition-colors duration-200">Bayar Cicilan</button>
                          <button data-testid={`payoff-${p.id}`} onClick={() => markPaidOff(p)} className="text-[10px] text-zinc-500 font-semibold uppercase tracking-wider hover:text-zinc-300 transition-colors duration-200">Tandai Lunas</button>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-400">{fmtDate(p.date)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      {p.status !== "cancelled" && user?.role !== "admin_cabang" && (
                        <button data-testid={`edit-purchase-${p.id}`} title="Edit Pembelian" onClick={() => setEditP(p)} className="text-zinc-600 hover:text-blue-500 transition-colors duration-200 p-1.5">
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {p.status !== "cancelled" && user?.role === "admin" && (
                        <button data-testid={`cancel-purchase-${p.id}`} title="Batalkan Pembelian (Refund)" onClick={() => setCancelP(p)} className="text-zinc-600 hover:text-blue-500 transition-colors duration-200 p-1.5">
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                      {user?.role === "admin" && (
                        <button data-testid={`delete-purchase-${p.id}`} onClick={() => remove(p.id)} className="text-zinc-600 hover:text-rose-500 transition-colors duration-200 p-1.5">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredPurchases.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-zinc-500">Tidak ada pembelian pada bulan ini.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <PurchaseDialog open={open} onOpenChange={setOpen} cars={eligible} onSaved={load} />
      <CancelDialog purchase={cancelP} branches={branches} onClose={() => setCancelP(null)} onSaved={load} />
      <EditPurchaseDialog purchase={editP} branches={branches} onClose={() => setEditP(null)} onSaved={load} />
      <PayPurchaseDialog purchase={payP} branches={branches} onClose={() => setPayP(null)} onSaved={load} />
    </div>
  );
}

export function PayPurchaseDialog({ purchase, branches, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const visible = user?.role === "admin_cabang" ? branches.filter((b) => b.id === user.branch_id || b.is_pusat) : branches;
  const sisa = purchase ? Math.max(0, purchase.price - (purchase.dp_amount || 0)) : 0;

  useEffect(() => {
    if (purchase) setForm({ amount: "", date: today(), cash_branch_id: user?.role === "admin_cabang" ? user.branch_id : purchase.cash_branch_id || purchase.branch_id || branches[0]?.id || "", description: "" });
  }, [purchase, user, branches]);

  if (!purchase || !form) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/purchases/${purchase.id}/pay`, { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success(r.data.paid_off ? "Pembayaran dicatat — pembelian LUNAS" : "Cicilan pembelian dicatat ke buku kas");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!purchase} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Bayar Cicilan Pembelian</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400 -mt-2">{purchase.car_name}{purchase.seller_name ? ` · Penjual: ${purchase.seller_name}` : ""}</p>
        <div className="grid grid-cols-3 gap-2 text-xs bg-[#0F0F12] border border-[#1A1A22] rounded-md p-3" data-testid="pay-purchase-summary">
          <div><p className="text-zinc-500">Harga Beli</p><Money value={purchase.price} className="text-zinc-200" /></div>
          <div><p className="text-zinc-500">Sudah Dibayar</p><Money value={purchase.dp_amount || 0} className="text-emerald-500" /></div>
          <div><p className="text-zinc-500">Sisa Hutang</p><Money value={sisa} className="text-blue-500" /></div>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="pay-purchase-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className={labelCls}>Jumlah Bayar (Rp)</Label>
                <button type="button" data-testid="pay-purchase-fill-sisa" onClick={() => setForm((f) => ({ ...f, amount: String(sisa) }))} className="text-[10px] text-blue-500 hover:text-blue-400 uppercase tracking-wider">Lunasi semua</button>
              </div>
              <Input data-testid="pay-purchase-amount-input" type="number" min="1" max={sisa} required value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="20000000" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Tanggal</Label>
              <Input data-testid="pay-purchase-date-input" type="date" required value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Keluar dari Kas</Label>
            <Select value={form.cash_branch_id} onValueChange={(v) => setForm((f) => ({ ...f, cash_branch_id: v }))}>
              <SelectTrigger data-testid="pay-purchase-branch-select" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                <SelectItem value="umum">Kas Umum (Tanpa Cabang)</SelectItem>
                {visible.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Keterangan</Label>
            <Input data-testid="pay-purchase-desc-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Termin 2 / pelunasan" className={inputCls} />
          </div>
          <Button data-testid="pay-purchase-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan ke Buku Kas"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ purchase, branches, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (purchase) setReason("");
  }, [purchase]);

  if (!purchase) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/purchases/${purchase.id}/cancel`, { reason });
      toast.success("Pembelian dibatalkan");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!purchase} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-sm">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Batalkan Pembelian</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400 -mt-2">{purchase.car_name}</p>
        <p className="text-xs text-zinc-500">Mobil kembali ke status Dalam Persiapan dan pembelian dikeluarkan dari laporan. Tidak ada arus kas yang dicatat.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="cancel-purchase-form">
          <div className="space-y-2">
            <Label className={labelCls}>Alasan Pembatalan</Label>
            <Input data-testid="cancel-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Contoh: unit tidak sesuai kesepakatan" className={inputCls} />
          </div>
          <Button data-testid="cancel-submit-button" type="submit" disabled={busy} className="w-full bg-rose-600 hover:bg-rose-500 text-white font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Batalkan Pembelian"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditPurchaseDialog({ purchase, branches, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (purchase) {
      setForm({
        payment_type: purchase.payment_type, price: String(purchase.price), dp_amount: String(purchase.dp_amount || ""),
        paid_off: !!purchase.paid_off, date: purchase.date, seller_name: purchase.seller_name || "", notes: purchase.notes || "",
        tax_status: purchase.tax_status || "", tax_due_date: purchase.tax_due_date || "", tax_amount: purchase.tax_amount ? String(purchase.tax_amount) : "",
        cash_branch_id: purchase.cash_branch_id || "",
      });
    }
  }, [purchase]);

  if (!purchase || !form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/purchases/${purchase.id}`, {
        payment_type: form.payment_type,
        price: parseFloat(form.price) || 0,
        dp_amount: form.payment_type === "dp" ? parseFloat(form.dp_amount) || 0 : 0,
        paid_off: form.payment_type === "cash" ? true : !!form.paid_off,
        date: form.date,
        seller_name: form.seller_name,
        notes: form.notes,
        tax_status: form.tax_status || null,
        tax_due_date: form.tax_due_date || null,
        tax_amount: parseFloat(form.tax_amount) || null,
        cash_branch_id: form.cash_branch_id || null,
      });
      toast.success("Pembelian diperbarui");
      onClose();
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!purchase} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Edit Pembelian</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400 -mt-2">{purchase.car_name}</p>
        <form onSubmit={submit} className="space-y-4" data-testid="edit-purchase-form">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Tipe Pembayaran</Label>
              <Select value={form.payment_type} onValueChange={(v) => set("payment_type", v)}>
                <SelectTrigger data-testid="edit-purchase-type-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                  {Object.entries(PURCHASE_PAYMENT_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Tanggal</Label>
              <Input data-testid="edit-purchase-date-input" type="date" required value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Harga Beli (Rp)</Label>
            <Input data-testid="edit-purchase-price-input" type="number" min="1" required value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} />
          </div>
          {form.payment_type === "dp" && (
            <>
              <div className="space-y-2">
                <Label className={labelCls}>Jumlah DP (Rp)</Label>
                <Input data-testid="edit-purchase-dp-input" type="number" min="0" value={form.dp_amount} onChange={(e) => set("dp_amount", e.target.value)} className={inputCls} />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="edit_paid_off" data-testid="edit-purchase-paidoff-checkbox" checked={form.paid_off} onCheckedChange={(v) => set("paid_off", !!v)} />
                <Label htmlFor="edit_paid_off" className="text-sm text-zinc-300">Sudah lunas</Label>
              </div>
            </>
          )}
          <div className="space-y-2">
            <Label className={labelCls}>Nama Penjual / Sumber</Label>
            <Input data-testid="edit-purchase-seller-input" value={form.seller_name} onChange={(e) => set("seller_name", e.target.value)} className={inputCls} />
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Sumber Dana (Buku Kas)</Label>
            <Select value={form.cash_branch_id} onValueChange={(v) => set("cash_branch_id", v)}>
              <SelectTrigger data-testid="edit-purchase-cash-branch-select" className={inputCls}><SelectValue placeholder="Pilih sumber dana" /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                {user?.role !== "admin_cabang" && <SelectItem value="umum">Kas Umum Pusat (Tanpa Cabang)</SelectItem>}
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-zinc-500">Mengubah ini memindahkan catatan kas keluar pembelian ke buku kas terpilih.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={labelCls}>Status Pajak</Label>
              <Select value={form.tax_status} onValueChange={(v) => set("tax_status", v)}>
                <SelectTrigger data-testid="edit-purchase-tax-status-select" className={inputCls}><SelectValue placeholder="Pilih status" /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                  <SelectItem value="hidup">Hidup</SelectItem>
                  <SelectItem value="mati">Mati</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Pajak Berlaku s.d.</Label>
              <Input data-testid="edit-purchase-tax-date-input" type="date" value={form.tax_due_date} onChange={(e) => set("tax_due_date", e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="space-y-2">
            <Label className={labelCls}>Estimasi Biaya (Rp) · Opsional</Label>
            <Input data-testid="edit-purchase-tax-amount-input" type="number" min="0" value={form.tax_amount} onChange={(e) => set("tax_amount", e.target.value)} className={inputCls} />
          </div>
          <Button data-testid="edit-purchase-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Perubahan"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
